import { useState, useEffect } from "react";
import aiApi from "@/api/ai";
import { useAppDispatch } from "@/hooks/types";
import { AIModelConfig } from "@/pages/Admin/AI/AIPreferencesConfig";
import { ModelConfigSettings } from "@/pages/Admin/AI/ModelConfigSettings";
import { AIToolHeader } from "@/pages/Admin/AI/components/AIToolHeader";
import { AdminResumeSelector } from "@/pages/Admin/AI/components/AdminResumeSelector";
import { AiResponsePreview } from "@/pages/Admin/AI/components/AiResponsePreview";
import { AiHistoryEntry, AIModel } from "@/types/ai";
import { getIsProductionEnv } from "@/utils/env";

type ScanMode =
    | "contentQuality"
    | "atsCompatibility"
    | "writingAndLanguage"
    | "formatAndDesign";

type AiResponse = Record<string, any>;

interface AdminResumeScannerProps {
    modelConfig: AIModelConfig;
    setModelConfig: (modelConfig: AIModelConfig) => void;
    addToHistory: (entry: AiHistoryEntry) => void;
}

const SCAN_MODES: { value: ScanMode; label: string; description: string }[] = [
    {
        value: "contentQuality",
        label: "Content Quality",
        description:
            "Evaluate work experience relevance, skills alignment, and summary impact",
    },
    {
        value: "atsCompatibility",
        label: "ATS Compatibility",
        description: "Assess keyword optimization and standard formatting",
    },
    {
        value: "writingAndLanguage",
        label: "Writing & Language",
        description: "Check grammar, action verbs, and clarity",
    },
    {
        value: "formatAndDesign",
        label: "Format & Design",
        description: "Evaluate visual hierarchy, consistency, and readability",
    },
];

export const AdminResumeScanner = ({
    modelConfig,
    setModelConfig,
    addToHistory,
}: AdminResumeScannerProps) => {
    const isProdEnv = getIsProductionEnv();
    const [selectedResumeId, setSelectedResumeId] = useState<string | null>(
        null,
    );
    const [selectedScanModes, setSelectedScanModes] = useState<ScanMode[]>([
        "contentQuality",
        "atsCompatibility",
        "writingAndLanguage",
        "formatAndDesign",
    ]);
    const [aiResponse, setAiResponse] = useState<AiResponse | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timer, setTimer] = useState<number>(0);
    const [timerActive, setTimerActive] = useState(false);
    const dispatch = useAppDispatch();
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (timerActive) {
            interval = setInterval(() => {
                setTimer(prev => prev + 0.1);
            }, 100);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timerActive]);

    const handleClear = () => {
        setSelectedResumeId(null);
        setAiResponse(null);
        setError(null);
        setTimer(0);
        setTimerActive(false);
    };

    const handleScanModeToggle = (mode: ScanMode) => {
        setSelectedScanModes(prev =>
            prev.includes(mode)
                ? prev.filter(m => m !== mode)
                : [...prev, mode],
        );
    };

    const handleGenerate = async () => {
        if (isProdEnv) {
            setError(
                "This feature is not available for testing in production. Test on staging",
            );
            return;
        }

        if (!selectedResumeId) {
            setError("Please select a resume to scan");
            return;
        }

        if (selectedScanModes.length === 0) {
            setError("Please select at least one scan mode");
            return;
        }

        setIsGenerating(true);
        setError(null);
        setTimer(0);
        setTimerActive(true);

        try {
            const response = await aiApi.scanResume(
                {
                    resume: selectedResumeId,
                    scanModes: selectedScanModes,
                    model: modelConfig.model as AIModel,
                    temperature: modelConfig.temperature,
                },
                dispatch,
            );
            setAiResponse(response);
            addToHistory({
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                tool: "ai-scan-resume",
                input: {
                    resumeId: selectedResumeId,
                    scanModes: selectedScanModes,
                    temperature: modelConfig.temperature,
                },
                output: response,
                modelUsed: modelConfig.model,
                duration: timer,
            });
        } catch (error) {
            console.error("Error generating response:", error);
            setAiResponse(null);
            setError(
                error instanceof Error
                    ? error.message
                    : "An unknown error occurred",
            );
        } finally {
            setIsGenerating(false);
            setTimerActive(false);
        }
    };

    return (
        <>
            <AIToolHeader activeTab="ai-scan-resume" />
            <div className="grid grid-cols-12 gap-8 px-8">
                {/* Left Column - Inputs */}
                <div className="col-span-5 space-y-6">
                    <ModelConfigSettings
                        modelConfig={modelConfig}
                        setModelConfig={setModelConfig}
                    />

                    <AdminResumeSelector
                        selectedResumeId={selectedResumeId}
                        onResumeSelect={setSelectedResumeId}
                    />

                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                            Scan Modes *
                        </label>
                        <div className="space-y-2">
                            {SCAN_MODES.map(mode => (
                                <label
                                    key={mode.value}
                                    className="flex items-start space-x-3"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedScanModes.includes(
                                            mode.value,
                                        )}
                                        onChange={() =>
                                            handleScanModeToggle(mode.value)
                                        }
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-700">
                                            {mode.label}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {mode.description}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column - AI Response */}
                <div className="col-span-7 rounded-lg border p-4">
                    <div className="mb-4 flex items-center justify-between space-x-4">
                        <h2 className="text-xl font-semibold">
                            ATS Scan Results
                        </h2>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleClear}
                                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleGenerate}
                                disabled={
                                    !selectedResumeId ||
                                    selectedScanModes.length === 0 ||
                                    isGenerating
                                }
                                className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                                    !selectedResumeId ||
                                    selectedScanModes.length === 0 ||
                                    isGenerating
                                        ? "cursor-not-allowed bg-primary-400"
                                        : "bg-primary-600 hover:bg-primary-700"
                                }`}
                            >
                                {isGenerating ? "Scanning..." : "Scan Resume"}
                            </button>
                            <span className="text-xs text-gray-500">
                                {timer > 0 && `${timer.toFixed(1)}s`}
                            </span>
                        </div>
                    </div>

                    <div className="min-h-[450px] overflow-auto rounded-lg border border-gray-300 bg-white p-4">
                        {isGenerating ? (
                            <div className="flex h-full items-center justify-center">
                                <p className="text-sm text-gray-500">
                                    Scanning resume...
                                </p>
                            </div>
                        ) : aiResponse ? (
                            <AiResponsePreview
                                aiResponse={aiResponse}
                                includeSectionKeys={false}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center">
                                <p className="text-sm text-gray-500">
                                    ATS scan results will appear here
                                </p>
                            </div>
                        )}
                        {error && (
                            <div className="mt-4 rounded-lg bg-red-50 p-4">
                                <p className="text-sm text-red-500">{error}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
